import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserAgentService } from '@shared/services';
import { take } from 'rxjs';

/**
 * На iOS при касании вне инпута скрывает клавиатуру, не закрывая модалку/оверлей.
 * Предвосхищая вопрос, почему только iOS - на андроиде обычно все в порядке
 */
@Directive({
  selector: 'input[sdBlurOnTouchOutside], textarea[sdBlurOnTouchOutside]',
  standalone: true,
})
export class BlurOnTouchOutsideDirective implements OnInit, OnChanges, OnDestroy {
  @Input({ transform: booleanAttribute }) sdBlurOnTouchOutside = true;

  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLInputElement | HTMLTextAreaElement>);
  private readonly userAgentService = inject(UserAgentService);
  private readonly destroyRef = inject(DestroyRef);

  private touchListener: ((e: TouchEvent) => void) | null = null;

  ngOnInit(): void {
    if (this.sdBlurOnTouchOutside) {
      this.addListenerIfNeeded();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['sdBlurOnTouchOutside']) return;

    if (this.sdBlurOnTouchOutside) {
      this.addListenerIfNeeded();
    } else {
      this.removeListener();
    }
  }

  ngOnDestroy(): void {
    this.removeListener();
  }

  private addListenerIfNeeded(): void {
    if (this.touchListener) return;

    this.userAgentService
      .isIOS()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((isIos) => {
        if (!isIos || !this.document.defaultView || !this.sdBlurOnTouchOutside) return;

        const input = this.elementRef.nativeElement;
        this.touchListener = (e: TouchEvent): void => {
          if (this.document.activeElement !== input) return;
          if (input.contains(e.target as Node)) return;
          input.blur();
        };
        this.document.addEventListener('touchstart', this.touchListener, { capture: true, passive: true });
      });
  }

  private removeListener(): void {
    if (!this.touchListener) return;
    this.document.removeEventListener('touchstart', this.touchListener, true);
    this.touchListener = null;
  }
}
